"use client";

import { AnimatedElement } from "@/components/marketing/AnimatedSection";
import { useLang } from "@/contexts/language-context";

/**
 * Tres pasos. El tercero es el que nos diferencia y el que más gente pregunta:
 * el enlace llega al CORREO, no se abre en la tablet del mostrador, porque el
 * formulario pide SSN, EIN y datos bancarios.
 */
export default function CapitalHowItWorks() {
  const { t } = useLang();

  const steps = [
    {
      n: "01",
      title: t("You sell. We measure.", "Vendes. Nosotros medimos."),
      body: t(
        "Every paid order counts. No forms, no spreadsheets, nothing extra to do — the counter starts on its own with your first sale.",
        "Cada orden pagada cuenta. Sin formularios, sin hojas de cálculo, sin nada extra que hacer — el contador arranca solo con tu primera venta."
      ),
    },
    {
      n: "02",
      title: t("At 90 days, we estimate.", "A los 90 días, estimamos."),
      body: t(
        "A full quarter is what a lender asks for in bank statements. With it we project your annual run rate and show which of the five options your numbers reach — and which they do not.",
        "Un trimestre completo es lo que un prestamista pide en estados de cuenta. Con él proyectamos tu corrida anual y te mostramos cuáles de las cinco opciones alcanzan tus números — y cuáles no."
      ),
    },
    {
      n: "03",
      title: t("We email you the link.", "Te mandamos el enlace al correo."),
      body: t(
        "Not to the counter tablet. The application asks for your Social Security number, your EIN and your bank details — that belongs on your personal phone, not on the device your staff uses all day.",
        "No a la tablet del mostrador. La solicitud pide tu número de seguro social, tu EIN y tus datos bancarios — eso va en tu teléfono personal, no en el equipo que usa tu personal todo el día."
      ),
    },
  ];

  return (
    <section id="how" className="relative py-24 px-6 bg-white">
      <div className="max-w-[1200px] mx-auto">
        <div className="max-w-[620px] mb-16">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("How it works", "Cómo funciona")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">
            {t("You already did the hard part.", "La parte difícil ya la hiciste.")}
          </h2>
          <p className="text-lg text-redy-secondary mt-5 leading-relaxed font-medium">
            {t(
              "You ran the restaurant. The paperwork a lender needs is the part we can take off your hands.",
              "Echaste a andar el restaurante. El papeleo que pide un prestamista es la parte que te podemos quitar de encima."
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {/* AnimatedElement y no `whileInView` crudo: el wrapper del repo trae
              un temporizador de respaldo que fuerza la visibilidad si el
              IntersectionObserver no dispara. Sin él las tarjetas se quedan en
              opacity 0 y la sección sale EN BLANCO — verificado aquí mismo. */}
          {steps.map((s, i) => (
            <AnimatedElement key={s.n} delay={i * 0.1}>
              <div className="flex items-baseline gap-3 mb-4">
                <span
                  className="text-[40px] font-black leading-none"
                  style={{
                    background: "linear-gradient(135deg,#D7263D,#F4722B)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {s.n}
                </span>
                <span className="flex-1 h-px bg-black/8" />
              </div>
              <h3 className="text-[21px] font-black text-redy-charcoal leading-snug">{s.title}</h3>
              <p className="text-[15px] text-redy-secondary leading-relaxed mt-2.5">{s.body}</p>
            </AnimatedElement>
          ))}
        </div>
      </div>
    </section>
  );
}
